"use client";

import { useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import type { PromptCategory, PromptSummary } from "@/lib/promptai";
import { categoryColorClass, buildPromptSlugMap } from "@/lib/promptai";

export default function PromptsExplorer({
  prompts,
  categories,
}: {
  prompts: PromptSummary[];
  categories: PromptCategory[];
}) {
  const [query, setQuery] = useState("");
  const [categorySlug, setCategorySlug] = useState<string>("all");
  const slugMap = useMemo(() => buildPromptSlugMap(prompts), [prompts]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return prompts.filter((p) => {
      const matchesCategory = categorySlug === "all" || p.category_id === categories.find((c) => c.slug === categorySlug)?.id;
      const matchesQuery =
        !q || p.title.toLowerCase().includes(q) || p.description.toLowerCase().includes(q) || p.category_name.toLowerCase().includes(q);
      return matchesCategory && matchesQuery;
    });
  }, [prompts, categories, query, categorySlug]);

  return (
    <div>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2 overflow-x-auto pb-1">
          <button
            type="button"
            onClick={() => setCategorySlug("all")}
            className={`flex-shrink-0 rounded-full px-4 py-1.5 text-sm font-medium transition ${
              categorySlug === "all"
                ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                : "border border-slate-200 text-slate-600 hover:border-violet-300 hover:text-violet-700 dark:border-slate-700 dark:text-slate-300 dark:hover:border-violet-500 dark:hover:text-violet-400"
            }`}
          >
            All ({prompts.length})
          </button>
          {categories.map((c) => (
            <button
              key={c.slug}
              type="button"
              onClick={() => setCategorySlug(c.slug)}
              className={`flex-shrink-0 rounded-full px-4 py-1.5 text-sm font-medium transition ${
                categorySlug === c.slug
                  ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                  : "border border-slate-200 text-slate-600 hover:border-violet-300 hover:text-violet-700 dark:border-slate-700 dark:text-slate-300 dark:hover:border-violet-500 dark:hover:text-violet-400"
              }`}
            >
              {c.name} ({c.prompt_count})
            </button>
          ))}
        </div>

        <div className="relative w-full flex-shrink-0 sm:w-64">
          <svg
            className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search prompts..."
            className="w-full rounded-full border border-slate-200 bg-white py-2 pl-9 pr-4 text-sm text-slate-700 focus:border-violet-400 focus:outline-none dark:border-slate-700 dark:bg-[#0f172a] dark:text-slate-200"
          />
        </div>
      </div>

      {filtered.length ? (
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {filtered.map((p) => (
            <Link
              key={p.id}
              href={`/prompts/${slugMap.get(p.id) ?? p.id}`}
              className="group overflow-hidden rounded-2xl border border-slate-200 bg-white/80 backdrop-blur-sm transition hover:-translate-y-1 hover:shadow-xl dark:border-slate-800 dark:bg-[#0f172a]/80"
            >
              <div className="relative aspect-[3/4] w-full bg-slate-100 dark:bg-slate-800">
                <Image
                  src={p.image_url}
                  alt={`${p.title} — AI image prompt example`}
                  fill
                  className="object-cover"
                  unoptimized
                  sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw"
                />
                <span
                  className={`absolute left-2 top-2 rounded-full px-2.5 py-1 text-[11px] font-semibold backdrop-blur ${categoryColorClass(p.category_color_index)}`}
                >
                  {p.category_name}
                </span>
              </div>
              <div className="p-3.5">
                <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">{p.title}</p>
                <p className="mt-1 line-clamp-2 text-xs text-slate-500 dark:text-slate-400">{p.description}</p>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <p className="mt-10 text-center text-sm text-slate-500 dark:text-slate-400">
          No prompts match &ldquo;{query}&rdquo;. Try a different search or category.
        </p>
      )}
    </div>
  );
}
